/**
 * Dialog for loading templates from WebCharts Antigo (LegacyStorage) and, behind
 * the `silverlightImport` feature flag, importing templates from the Silverlight
 * legacy app (SilverlightStorage → JSON ChartTpl → convertSilverlightChartTpl).
 */

import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";
import { TabView, TabPanel } from "primereact/tabview";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import { isFeatureEnabled } from "../../data/Services/settings";
import "./DesktopManager.css";

const LoadLegacyTemplateDialog = ({
  visible,
  onHide,
  onImport,
  onImportSilverlight,
}) => {
  const [templates, setTemplates] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  // Silverlight tab state (only used when the flag is on)
  const [silverlightTemplates, setSilverlightTemplates] = useState([]);
  const [silverlightLoading, setSilverlightLoading] = useState(false);
  const [silverlightError, setSilverlightError] = useState("");

  // Read at USE time (render), never at module scope
  const silverlightEnabled = isFeatureEnabled("silverlightImport");

  // Load legacy templates when dialog opens
  useEffect(() => {
    if (visible) {
      loadLegacyTemplates();
      if (isFeatureEnabled("silverlightImport")) {
        loadSilverlightTemplates();
      }
    }
  }, [visible]);

  const loadLegacyTemplates = async () => {
    setLoading(true);
    setError("");

    try {
      // Import services dynamically to avoid circular dependencies
      const { default: storageService } = await import(
        "~/data/Services/storageService"
      );
      const { default: marketdata } = await import(
        "~/data/Services/marketdata"
      );

      // Get a valid token (will refresh if expired)
      console.log(
        "[LoadLegacyTemplateDialog] Getting valid marketdata token...",
      );
      const token = await marketdata.getValidToken();

      if (!token) {
        throw new Error(
          "Não foi possível obter token de autenticação. Por favor, faça login novamente.",
        );
      }

      // Initialize storageService with the valid token
      const url = marketdata.getUrl();
      console.log(
        "[LoadLegacyTemplateDialog] Initializing storageService with valid token...",
      );
      storageService.init(url, token);

      console.log("[LoadLegacyTemplateDialog] Loading legacy templates...");
      const list = await storageService.getLegacyTemplateList();

      console.log(
        "[LoadLegacyTemplateDialog] Got legacy templates:",
        list?.length || 0,
      );
      setTemplates(list || []);
    } catch (err) {
      console.error(
        "[LoadLegacyTemplateDialog] Error loading legacy templates:",
        err,
      );
      setError(
        err.message ||
          "Falha ao carregar lista de templates do WebCharts Antigo",
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSilverlightTemplates = async () => {
    setSilverlightLoading(true);
    setSilverlightError("");

    try {
      const { default: storageService } = await import(
        "~/data/Services/storageService"
      );
      const { default: marketdata } = await import(
        "~/data/Services/marketdata"
      );
      const token = await marketdata.getValidToken();
      if (!token) {
        throw new Error(
          "Não foi possível obter token de autenticação. Por favor, faça login novamente.",
        );
      }
      storageService.init(marketdata.getUrl(), token);

      console.log(
        "[LoadLegacyTemplateDialog] Loading silverlight templates...",
      );
      const list = await storageService.getSilverlightTemplateList();
      console.log(
        "[LoadLegacyTemplateDialog] Got silverlight templates:",
        list?.length || 0,
      );
      setSilverlightTemplates(list || []);
    } catch (err) {
      console.error(
        "[LoadLegacyTemplateDialog] Error loading silverlight templates:",
        err,
      );
      setSilverlightError(
        err.message || "Falha ao carregar lista de templates do Silverlight",
      );
      setSilverlightTemplates([]);
    } finally {
      setSilverlightLoading(false);
    }
  };

  const reloadLists = () => {
    loadLegacyTemplates();
    if (isFeatureEnabled("silverlightImport")) {
      loadSilverlightTemplates();
    }
  };

  const handleImport = async (template) => {
    try {
      console.log(
        "[LoadLegacyTemplateDialog] Importing legacy template:",
        template.chartid,
        template.name,
      );
      await onImport(template.chartid, template.name);
      onHide(); // Close dialog after successful import
    } catch (err) {
      console.error(
        "[LoadLegacyTemplateDialog] Error importing template:",
        err,
      );
      setError(err.message || "Falha ao importar template");
    }
  };

  const handleSilverlightImport = async (template) => {
    try {
      console.log(
        "[LoadLegacyTemplateDialog] Importing silverlight template:",
        template.name,
      );
      // PK do storage Silverlight é (login, name) — o name É o identificador
      await onImportSilverlight(template.name);
      onHide();
    } catch (err) {
      console.error(
        "[LoadLegacyTemplateDialog] Error importing silverlight template:",
        err,
      );
      setSilverlightError(err.message || "Falha ao importar template");
    }
  };

  const formatDate = (timestamp) => {
    try {
      // Check if timestamp is Unix timestamp (seconds since epoch)
      let date;
      if (typeof timestamp === "number" && timestamp < 4102444800000) {
        // Convert from seconds to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }

      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  // Column templates
  const nameTemplate = (rowData) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          cursor: "pointer",
        }}
        onDoubleClick={() => handleImport(rowData)}
      >
        <i
          className="pi pi-file-import"
          style={{ fontSize: "12px", color: "var(--orange-500)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name}
        </span>
      </div>
    );
  };

  const dateTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
        }}
      >
        {formatDate(rowData.timestamp)}
      </span>
    );
  };

  const actionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <Button
          icon="pi pi-download"
          label="Importar"
          className="p-button-sm p-button-success legacy-template-action-btn-import"
          onClick={() => handleImport(rowData)}
          data-pr-tooltip="Importar template do WebCharts Antigo"
          data-pr-position="top"
          style={{ fontSize: "11px" }}
          data-evo-id={`loadlegacytemplatedialog-btn-import-${rowData.chartid}`}
        />
      </div>
    );
  };

  // Silverlight column templates — charttemplates GUARDA last_update (ao
  // contrário do desktop), então a coluna Data existe aqui
  const silverlightNameTemplate = (rowData) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          cursor: "pointer",
        }}
        onDoubleClick={() => handleSilverlightImport(rowData)}
        data-evo-id={`loadlegacytemplatedialog-silverlight-row-${rowData.name}`}
      >
        <i
          className="pi pi-file-import"
          style={{ fontSize: "12px", color: "var(--orange-500)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name}
        </span>
      </div>
    );
  };

  const silverlightDateTemplate = (rowData) => {
    return (
      <span style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>
        {rowData.lastUpdate ? formatDate(rowData.lastUpdate) : ""}
      </span>
    );
  };

  const silverlightActionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <Button
          icon="pi pi-download"
          label="Importar"
          className="p-button-sm p-button-success silverlight-template-action-btn-import"
          onClick={() => handleSilverlightImport(rowData)}
          data-pr-tooltip="Importar e converter template do Silverlight"
          data-pr-position="top"
          style={{ fontSize: "11px" }}
          data-evo-id="loadlegacytemplatedialog-btn-import-silverlight"
        />
      </div>
    );
  };

  const headerWithSearch = (
    <div className="desktop-dialog-header-inline">
      <i
        className="pi pi-history"
        style={{ color: "var(--orange-500)", fontSize: "0.875rem" }}
      ></i>
      <span className="desktop-dialog-title">
        {silverlightEnabled
          ? "Importar templates antigos"
          : "Importar do WebCharts Antigo"}
      </span>
      <div className="desktop-header-search">
        <i className="pi pi-search" />
        <input
          type="text"
          autoComplete="off"
          className="p-inputtext p-component"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          data-evo-id="loadlegacytemplatedialog-input-filter"
        />
      </div>
      <Button
        icon="pi pi-refresh"
        className="p-button-text p-button-sm p-button-rounded legacy-template-refresh-btn"
        onClick={reloadLists}
        disabled={loading || silverlightLoading}
        data-pr-tooltip="Atualizar lista"
        data-pr-position="top"
        style={{ width: "26px", height: "26px", padding: "0" }}
        data-evo-id="loadlegacytemplatedialog-btn-refresh"
      />
    </div>
  );

  const emptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum template encontrado com esse nome"
          : "Nenhum template do WebCharts Antigo encontrado"}
      </p>
      <p className="desktop-empty-hint">
        Os templates salvos no WebCharts Antigo aparecerão aqui
      </p>
    </div>
  );

  const silverlightEmptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum template encontrado com esse nome"
          : "Nenhum template do Silverlight encontrado"}
      </p>
      <p className="desktop-empty-hint">
        Os templates salvos no aplicativo Silverlight aparecerão aqui
      </p>
    </div>
  );

  const legacyTabContent = (
    <>
      {/* Info Message */}
      <Message
        severity="info"
        text="Selecione um template do WebCharts Antigo para importar. O template será convertido para o formato atual."
        className="desktop-info-message"
      />

      {/* Error Message */}
      {error && (
        <Message
          severity="error"
          text={error}
          className="desktop-error-message"
        />
      )}

      {/* Template Table */}
      {loading ? (
        <div className="desktop-loading-state">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <p>Carregando templates do WebCharts Antigo...</p>
        </div>
      ) : (
        <DataTable
          value={templates}
          globalFilter={globalFilter}
          emptyMessage={emptyMessage}
          scrollable
          showGridlines
          stripedRows
          size="small"
          className="desktop-table"
        >
          <Column
            field="name"
            header="Nome"
            body={nameTemplate}
            sortable
            headerClassName="desktop-col-name"
            bodyClassName="desktop-col-name"
          />
          <Column
            field="timestamp"
            header="Data"
            body={dateTemplate}
            sortable
            style={{ width: "130px" }}
            headerClassName="desktop-col-date"
            bodyClassName="desktop-col-date"
          />
          <Column
            header="Ações"
            body={actionsTemplate}
            style={{ width: "120px", textAlign: "center" }}
            headerClassName="desktop-col-actions"
            bodyClassName="desktop-col-actions"
          />
        </DataTable>
      )}
    </>
  );

  const silverlightTabContent = (
    <>
      <Message
        severity="info"
        text="Selecione um template do aplicativo Silverlight para importar. Os indicadores e o tipo de gráfico serão aplicados ao gráfico selecionado."
        className="desktop-info-message"
      />

      {silverlightError && (
        <Message
          severity="error"
          text={silverlightError}
          className="desktop-error-message"
        />
      )}

      {silverlightLoading ? (
        <div className="desktop-loading-state">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <p>Carregando templates do Silverlight...</p>
        </div>
      ) : (
        <DataTable
          value={silverlightTemplates}
          globalFilter={globalFilter}
          emptyMessage={silverlightEmptyMessage}
          scrollable
          showGridlines
          stripedRows
          size="small"
          className="desktop-table"
        >
          <Column
            field="name"
            header="Nome"
            body={silverlightNameTemplate}
            sortable
            headerClassName="desktop-col-name"
            bodyClassName="desktop-col-name"
          />
          <Column
            field="lastUpdate"
            header="Data"
            body={silverlightDateTemplate}
            sortable
            style={{ width: "130px" }}
            headerClassName="desktop-col-date"
            bodyClassName="desktop-col-date"
          />
          <Column
            header="Ações"
            body={silverlightActionsTemplate}
            style={{ width: "120px", textAlign: "center" }}
            headerClassName="desktop-col-actions"
            bodyClassName="desktop-col-actions"
          />
        </DataTable>
      )}
    </>
  );

  return (
    <>
      <Tooltip target=".legacy-template-action-btn-import" />
      <Tooltip target=".legacy-template-refresh-btn" />
      <Tooltip target=".silverlight-template-action-btn-import" />

      <ModalBase
        header={headerWithSearch}
        visible={visible}
        onHide={onHide}
        size="large"
        variant="data"
        footer={null}
        className="desktop-load-dialog-modal legacy-import-modal"
      >
        <div className="desktop-load-dialog">
          {silverlightEnabled ? (
            <TabView
              activeIndex={activeTab}
              onTabChange={(e) => setActiveTab(e.index)}
              className="legacy-import-tabview"
            >
              <TabPanel header="Legado" leftIcon="pi pi-history">
                {legacyTabContent}
              </TabPanel>
              <TabPanel header="Silverlight" leftIcon="pi pi-desktop">
                {silverlightTabContent}
              </TabPanel>
            </TabView>
          ) : (
            legacyTabContent
          )}
        </div>
      </ModalBase>
    </>
  );
};

LoadLegacyTemplateDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onImportSilverlight: PropTypes.func,
};

export default LoadLegacyTemplateDialog;
